import React, { useEffect, useMemo, useState } from "react";
import { webAlert, webConfirm } from "../webDialog";
import { useAuthStore } from "../../state/authStore";
import { authApi } from "../../api/auth";
import { childrenApi } from "../../api/children";
import { notificationsApi, NotificationPrefs } from "../../api/notifications";
import {
  pushSupported,
  currentPushState,
  enablePush,
  disablePush,
  PushState,
} from "../webPush";
import { CONTENT_CATEGORIES } from "../../data/categories";
import { mascotColors } from "../../theme";
import {
  MONTH_LABELS,
  ageFromBirthday,
  birthYearOptions,
} from "../../utils/birthday";
import { PlanInterval, SubscriptionState } from "../../api/subscriptions";
import { Icon } from "../components/Icon";
import { WebMascot } from "../components/WebMascot";
import { ChildWithProgress } from "../WebShell";

// ─────────────────────────────────────────────────────────────────────────
// WebFamilyAccount — the rich "Family account" settings page ported from
// the design handoff (`design_handoff_mapa/web-account.jsx`).
//
// The previous WebMe was a simple settings list; this replaces it with a
// tabbed layout matching the design: Profile | Plan & billing | Kids |
// Devices | Notifications | Privacy.
//
// Co-parent invitations are intentionally NOT included here — the user
// asked to skip that surface for now. The Profile tab is single-column.
//
// Where backend support exists (subscription, children, sign-out), the UI
// is wired to real data. Where it doesn't (2FA, device list, password
// change, billing history), the controls render but are non-destructive
// stubs (`alert("Coming soon")`) clearly marked with TODOs.
// ─────────────────────────────────────────────────────────────────────────

type TabKey =
  | "profile"
  | "plan"
  | "kids"
  | "devices"
  | "notify"
  | "privacy";

type Props = {
  // Kidly+ snapshot from the auth store. Drives the Plan tab status banner
  // and the "Subscribe / Manage" CTA copy.
  subscription?: SubscriptionState | null;
  // Full kid roster for the Kids tab grid. Passed by WebShell.
  kids: ChildWithProgress[];
  // Pull the email out of the JWT payload server-side once; the FE just
  // decodes the access token (no extra round-trip). Pass `null` if you
  // can't be bothered to decode — the field falls back to "—".
  email: string | null;

  onSignOut: () => void;
  // Permanently deletes the account (DELETE /v1/auth/me) then signs out.
  // Threaded to the Privacy tab's danger zone.
  onDeleteAccount: () => Promise<void> | void;
  // Opens the Stripe Customer Portal — for existing subscribers who want
  // to update card / cancel / resubscribe. The "Manage subscription"
  // button on the Plan tab routes here.
  onManageSubscription: () => void;
  // Start a fresh Stripe Checkout for the chosen plan. Used by inactive
  // parents. The host (WebShell) handles the URL redirect.
  onSubscribe: (plan: PlanInterval) => void;
  // Start the no-card 30-day free trial. Resolves once the entitlement is
  // granted; the host refreshes the cached subscription. Only surfaced when
  // `subscription.canStartFreeTrial` is true.
  onStartTrial?: () => Promise<void> | void;
  // Switch an existing subscription's plan (monthly ↔ annual). Pro-rates
  // server-side. The Plan tab waits for the returned promise so it can
  // show a "Switching…" indicator and surface errors inline.
  onSwitchPlan: (plan: PlanInterval) => Promise<void>;
  onPairTV: () => void;
  onAddChild: () => void;
  // Optional — when present, the Kids tab shows a "Switch" button per kid
  // so the parent can flip the active child without leaving the page.
  onSwitchChild?: (childId: string) => void;
  // Called after a kid profile is renamed or deleted so the host can refetch
  // the roster. Without it the Kids tab would keep showing stale names.
  onKidsChanged?: () => void;
  // Optional jump to the internal admin metrics dashboard. When set,
  // the Privacy tab shows a small "Operational metrics (internal)"
  // link. Backend gates access via ADMIN_EMAILS — non-admins who
  // follow the link see a 403 error inline on the dashboard, nothing
  // sensitive leaks.
  onGoAdminMetrics?: () => void;
};

const TABS: { key: TabKey; label: string }[] = [
  { key: "profile", label: "Profile" },
  { key: "plan", label: "Plan & billing" },
  { key: "kids", label: "Kids" },
  { key: "devices", label: "Devices" },
  { key: "notify", label: "Notifications" },
  { key: "privacy", label: "Privacy" },
];

export function WebFamilyAccount({
  subscription,
  kids,
  email,
  onSignOut,
  onDeleteAccount,
  onManageSubscription,
  onSubscribe,
  onStartTrial,
  onSwitchPlan,
  onPairTV,
  onAddChild,
  onSwitchChild,
  onKidsChanged,
  onGoAdminMetrics,
}: Props) {
  const [tab, setTab] = useState<TabKey>("profile");

  // Header summary line: "<email> · N kids · Kidly+ <status>"
  const summary = useMemo(() => {
    const parts: string[] = [];
    if (email) parts.push(email);
    parts.push(`${kids.length} ${kids.length === 1 ? "kid" : "kids"}`);
    if (subscription?.isActive) parts.push("Kidly+ active");
    else if (subscription?.status === "trialing") parts.push("Kidly+ trial");
    else parts.push("Free plan");
    return parts.join(" · ");
  }, [email, kids.length, subscription]);

  return (
    <div className="fadeup w-resppad" style={{ padding: "24px 32px 64px" }}>
      {/* Header */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "auto 1fr",
          gap: 18,
          alignItems: "center",
          marginBottom: 28,
        }}
      >
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: 22,
            background: "linear-gradient(135deg, var(--primary), #FFB774)",
            display: "grid",
            placeItems: "center",
            boxShadow:
              "inset 0 -4px 0 rgba(0,0,0,0.18), 0 10px 22px rgba(255,138,76,0.30)",
          }}
        >
          <WebMascot mood="happy" color="#fff" size={48} />
        </div>
        <div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 900,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "var(--primary-ink)",
            }}
          >
            Family account
          </div>
          <h1
            style={{
              fontFamily: "var(--display)",
              fontSize: 36,
              fontWeight: 900,
              letterSpacing: "-0.02em",
              color: "var(--ink)",
              margin: "4px 0 4px",
            }}
          >
            Your household
          </h1>
          <div
            style={{ fontSize: 14, fontWeight: 600, color: "var(--ink-3)" }}
          >
            {summary}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div
        style={{
          display: "flex",
          gap: 4,
          borderBottom: "1px solid var(--line)",
          marginBottom: 28,
          overflowX: "auto",
        }}
      >
        {TABS.map((tt) => (
          <button
            key={tt.key}
            onClick={() => setTab(tt.key)}
            style={{
              border: 0,
              background: "transparent",
              cursor: "pointer",
              font: "inherit",
              fontWeight: 800,
              fontSize: 14,
              color: tab === tt.key ? "var(--ink)" : "var(--ink-3)",
              padding: "12px 16px",
              borderBottom:
                "3px solid " +
                (tab === tt.key ? "var(--primary)" : "transparent"),
              marginBottom: -1,
              whiteSpace: "nowrap",
            }}
          >
            {tt.label}
          </button>
        ))}
      </div>

      {tab === "profile" && <ProfileTab email={email} />}
      {tab === "plan" && (
        <PlanTab
          subscription={subscription}
          onManageSubscription={onManageSubscription}
          onSubscribe={onSubscribe}
          onStartTrial={onStartTrial}
          onSwitchPlan={onSwitchPlan}
        />
      )}
      {tab === "kids" && (
        <KidsTab
          kids={kids}
          onAddChild={onAddChild}
          onSwitchChild={onSwitchChild}
          onKidsChanged={onKidsChanged}
        />
      )}
      {tab === "devices" && <DevicesTab onPairTV={onPairTV} />}
      {tab === "notify" && <NotifyTab />}
      {tab === "privacy" && (
        <PrivacyTab
          onSignOut={onSignOut}
          onDeleteAccount={onDeleteAccount}
          onGoAdminMetrics={onGoAdminMetrics}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Profile tab — single-column (no co-parent panel per user instruction).
// Email is read-only because the JWT subject is keyed off it; changing it
// requires a re-auth flow we don't have yet. Name is editable but the save
// ─────────────────────────────────────────────────────────────────────────
function ProfileTab({ email }: { email: string | null }) {
  const { accessToken, ttsEnabled, setTtsEnabled } = useAuthStore();
  const [name, setName] = useState("");
  // Language is local-only — no DB column for it yet. Persisting it would
  // need a Parent.locale field + migration; deferred until we actually
  // localize content.
  const [lang, setLang] = useState("English");
  const [twoFA, setTwoFA] = useState(false);
  // Live email — overrides the JWT-decoded fallback as soon as /me lands.
  // The JWT only carries the email at sign-in time; if the parent changes
  // it later (via a future re-auth flow) the live value will be correct.
  const [liveEmail, setLiveEmail] = useState<string | null>(email);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Change-password modal state. 2-step: enter new password first, then code.
  const [pwModal, setPwModal] = useState(false);
  const [pwStep, setPwStep] = useState<"password" | "code">("password");
  const [pwCode, setPwCode] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwDone, setPwDone] = useState(false);
  const [pwSending, setPwSending] = useState(false);

  // Hydrate on mount from /v1/auth/me — populates the name field that the
  // JWT doesn't carry. Best-effort: a network blip just leaves the form
  // empty (the parent can still type a value and Save).
  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    authApi
      .me(accessToken)
      .then((profile) => {
        if (cancelled) return;
        setName(profile.name ?? "");
        if (profile.email) setLiveEmail(profile.email);
      })
      .catch(() => {
        // Leave defaults in place; Save will surface a real error.
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  // Auto-clear the "Saved ✓" indicator after 2s.
  useEffect(() => {
    if (!savedAt) return;
    const t = setTimeout(() => setSavedAt(null), 2000);
    return () => clearTimeout(t);
  }, [savedAt]);

  const onSave = async () => {
    if (!accessToken) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await authApi.updateMe(
        { name: name.trim() || null },
        accessToken,
      );
      setName(updated.name ?? "");
      setSavedAt(Date.now());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't save profile.");
    } finally {
      setSaving(false);
    }
  };

  // Email a 6-digit code to the signed-in parent (works for Google accounts —
  // no current password needed). Same backend flow as the app's reset.
  const sendPwCode = async () => {
    if (!liveEmail) return;
    setPwSending(true);
    setPwError(null);
    try {
      await authApi.requestPasswordReset(liveEmail);
    } catch {
      // Neutral — never reveals account existence; user can resend.
    } finally {
      setPwSending(false);
    }
  };

  const onChangePassword = () => {
    setPwCode("");
    setNewPw("");
    setConfirmPw("");
    setPwError(null);
    setPwDone(false);
    setPwStep("password");
    setPwModal(true);
  };

  const advanceToCode = async () => {
    setPwError(null);
    if (newPw.length < 8) {
      setPwError("New password must be at least 8 characters.");
      return;
    }
    if (newPw !== confirmPw) {
      setPwError("Passwords don't match.");
      return;
    }
    setPwCode("");
    setPwStep("code");
    void sendPwCode();
  };

  const submitPassword = async () => {
    setPwError(null);
    if (pwCode.trim().length !== 6) {
      setPwError("Enter the 6-digit code from your email.");
      return;
    }
    setPwBusy(true);
    try {
      await authApi.resetPassword(liveEmail ?? "", pwCode.trim(), newPw);
      setPwDone(true);
      setTimeout(() => setPwModal(false), 1400);
    } catch (e) {
      setPwError(
        e instanceof Error
          ? e.message
          : "That code is invalid or has expired.",
      );
    } finally {
      setPwBusy(false);
    }
  };

  const pwInput: React.CSSProperties = {
    width: "100%",
    padding: "12px 14px",
    borderRadius: 12,
    border: "1px solid var(--line)",
    marginBottom: 10,
    fontSize: 14,
    fontWeight: 600,
    background: "var(--surface)",
    color: "var(--ink)",
    boxSizing: "border-box",
  };

  return (
    <div style={{ maxWidth: 720 }}>
      {pwModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: 16,
          }}
          onClick={() => !pwBusy && setPwModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "var(--surface)",
              borderRadius: 16,
              padding: 24,
              width: "100%",
              maxWidth: 380,
              boxShadow: "0 12px 40px rgba(0,0,0,0.22)",
            }}
          >
            <h3
              style={{
                margin: 0,
                marginBottom: 6,
                fontWeight: 900,
                fontSize: 18,
                color: "var(--ink)",
              }}
            >
              Change password
            </h3>
            {pwDone ? (
              <p style={{ color: "#3A8530", fontWeight: 800, margin: "8px 0" }}>
                Password updated ✓
              </p>
            ) : pwStep === "password" ? (
              <>
                <p
                  style={{
                    marginTop: 0,
                    marginBottom: 14,
                    fontSize: 13,
                    fontWeight: 500,
                    color: "var(--ink-3)",
                  }}
                >
                  Choose a new password for your account.
                </p>
                <input
                  type="password"
                  placeholder="New password"
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  style={pwInput}
                  autoComplete="new-password"
                  autoFocus
                />
                <input
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && advanceToCode()}
                  style={pwInput}
                  autoComplete="new-password"
                />
                {pwError ? (
                  <div
                    style={{
                      color: "#D2502E",
                      fontSize: 13,
                      fontWeight: 700,
                      marginBottom: 10,
                    }}
                  >
                    {pwError}
                  </div>
                ) : null}
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    justifyContent: "flex-end",
                    marginTop: 4,
                  }}
                >
                  <button
                    className="btn btn-ghost"
                    onClick={() => setPwModal(false)}
                  >
                    Cancel
                  </button>
                  <button className="btn" onClick={advanceToCode}>
                    Continue
                  </button>
                </div>
              </>
            ) : (
              <>
                <p
                  style={{
                    marginTop: 0,
                    marginBottom: 14,
                    fontSize: 13,
                    fontWeight: 500,
                    color: "var(--ink-3)",
                  }}
                >
                  {pwSending
                    ? `Emailing a 6-digit code to ${liveEmail ?? "your email"}…`
                    : `We emailed a 6-digit code to ${liveEmail ?? "your email"}.`}
                </p>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="6-digit code"
                  value={pwCode}
                  onChange={(e) => setPwCode(e.target.value.replace(/\D/g, ""))}
                  onKeyDown={(e) => e.key === "Enter" && submitPassword()}
                  style={{ ...pwInput, letterSpacing: 4, fontWeight: 800 }}
                  autoFocus
                />
                <button
                  onClick={sendPwCode}
                  disabled={pwSending}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    marginBottom: 12,
                    fontSize: 13,
                    fontWeight: 700,
                    color: "var(--primary-ink)",
                    cursor: "pointer",
                  }}
                >
                  {pwSending ? "Sending…" : "Resend code"}
                </button>
                {pwError ? (
                  <div
                    style={{
                      color: "#D2502E",
                      fontSize: 13,
                      fontWeight: 700,
                      marginBottom: 10,
                    }}
                  >
                    {pwError}
                  </div>
                ) : null}
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    justifyContent: "flex-end",
                    marginTop: 4,
                  }}
                >
                  <button
                    className="btn btn-ghost"
                    onClick={() => setPwStep("password")}
                    disabled={pwBusy}
                  >
                    Back
                  </button>
                  <button
                    className="btn"
                    onClick={submitPassword}
                    disabled={pwBusy}
                    style={pwBusy ? { opacity: 0.55 } : undefined}
                  >
                    {pwBusy ? "Saving…" : "Update password"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <div className="w-panel">
        <h3>Parent profile</h3>
        <AccField
          label="Your name"
          value={name}
          onChange={setName}
          placeholder="Add your name"
        />
        <AccField
          label="Email"
          value={liveEmail ?? ""}
          onChange={() => {}}
          type="email"
          disabled
        />
        <AccField label="Language" value={lang} onChange={setLang} />

        <div
          style={{
            marginTop: 14,
            paddingTop: 18,
            borderTop: "1px solid var(--line)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 10,
            }}
          >
            <div>
              <div
                style={{ fontWeight: 800, fontSize: 14, color: "var(--ink)" }}
              >
                Password
              </div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: "var(--ink-3)",
                }}
              >
                Keep your family's account safe with a strong password.
              </div>
            </div>
            <button
              className="btn btn-ghost"
              style={{
                background: "var(--surface)",
                padding: "10px 16px",
                fontSize: 13,
              }}
              onClick={onChangePassword}
            >
              Change password
            </button>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              paddingTop: 12,
              borderTop: "1px dashed var(--line)",
            }}
          >
            <div>
              <div
                style={{ fontWeight: 800, fontSize: 14, color: "var(--ink)" }}
              >
                Two-factor authentication
              </div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: "var(--ink-3)",
                }}
              >
                Extra protection for your family&apos;s account.
              </div>
            </div>
            <button
              className={"w-switch " + (twoFA ? "on" : "")}
              onClick={() => setTwoFA((v) => !v)}
              aria-label="Toggle two-factor authentication"
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 12,
            marginTop: 18,
          }}
        >
          {error && (
            <span
              style={{
                fontSize: 12.5,
                fontWeight: 700,
                color: "#C0392B",
              }}
            >
              {error}
            </span>
          )}
          {savedAt && !error && (
            <span
              style={{
                fontSize: 12.5,
                fontWeight: 800,
                color: "#3A8530",
              }}
            >
              Saved ✓
            </span>
          )}
          <button
            className="btn"
            onClick={onSave}
            disabled={saving}
            style={saving ? { opacity: 0.55 } : undefined}
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      {/* Kid experience — small accessibility / behavior toggles that apply
          to every kid profile on this device. Today this is just the TTS
          switch; future toggles (auto-skip intros, captions, …) live here too. */}
      <div className="w-panel" style={{ marginTop: 16 }}>
        <h3>Kid experience</h3>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
          }}
        >
          <div>
            <div
              style={{ fontWeight: 800, fontSize: 14, color: "var(--ink)" }}
            >
              Read questions aloud
            </div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: "var(--ink-3)",
              }}
            >
              Speaks the question aloud and shows the prompt. Turn it off for
              pre-readers — they'll see only the answer pictures, no text.
            </div>
          </div>
          <button
            className={"w-switch " + (ttsEnabled ? "on" : "")}
            onClick={() => setTtsEnabled(!ttsEnabled)}
            aria-label="Toggle read questions aloud"
          />
        </div>
      </div>
    </div>
  );
}

// Local editable form field. Falls back to a placeholder when value is "".
function AccField({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (s: string) => void;
  type?: string;
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label
        style={{
          display: "block",
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "var(--ink-3)",
          marginBottom: 6,
        }}
      >
        {label}
      </label>
      <input
        className="w-input"
        type={type}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        style={disabled ? { opacity: 0.7, cursor: "not-allowed" } : undefined}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Plan & billing tab — laid out per the design handoff: a 2-column grid
// with the status banner / features / billing history on the left and a
// Payment-method + Refer-a-friend column on the right.
//
// Real data we have today: subscription status, renewal date, trial-end
// date. We DON'T fetch Stripe card details or invoice history yet — the
// Payment-method and Billing-history panels render with an honest empty
// state pointing the parent at the Stripe Portal, where they can update
// the card and pull receipts. The structure mirrors the design so the
// surfaces light up naturally once we wire the invoices fetch.
// ─────────────────────────────────────────────────────────────────────────
function PlanTab({
  subscription,
  onManageSubscription,
  onSubscribe,
  onStartTrial,
  onSwitchPlan,
}: {
  subscription: SubscriptionState | null | undefined;
  onManageSubscription: () => void;
  onSubscribe: (plan: PlanInterval) => void;
  onStartTrial?: () => Promise<void> | void;
  onSwitchPlan: (plan: PlanInterval) => Promise<void>;
}) {
  const features = [
    "Unlimited cartoons",
    "No ads, ever",
    "Up to 4 kid profiles",
    "Weekly recap emails",
    "Bedtime stories",
    "Offline downloads",
  ];

  const isActive = !!subscription?.isActive;
  const status = subscription?.status ?? "inactive";
  const plan = subscription?.plan ?? null;
  const [switching, setSwitching] = useState<PlanInterval | null>(null);
  const [switchError, setSwitchError] = useState<string | null>(null);
  // No-card trial eligibility. Older servers omit the flag → treat a
  // never-subscribed parent as eligible.
  const trialEligible =
    !!onStartTrial &&
    (subscription?.canStartFreeTrial ??
      (!subscription || subscription.status === "inactive"));
  const [startingTrial, setStartingTrial] = useState(false);
  const handleStartTrial = async () => {
    if (!onStartTrial) return;
    setStartingTrial(true);
    try {
      await onStartTrial();
    } finally {
      setStartingTrial(false);
    }
  };

  const headline = (() => {
    // TEST PRICING — temporary $0.10/$0.96 to verify the live Stripe flow.
    // Flip back to "$95.88/year" / "$9.99/month" once confirmed (must match
    // the amounts on the Stripe Price objects).
    const priceLabel =
      plan === "annual"
        ? "$0.96/year"
        : plan === "monthly"
          ? "$0.10/month"
          : null;
    if (status === "active")
      return `Kidly+ Family${priceLabel ? ` · ${priceLabel}` : ""}`;
    if (status === "trialing")
      return `Kidly+ Family · Free trial${plan === "annual" ? " (annual)" : ""}`;
    if (status === "canceled") return "Kidly+ · Access ending soon";
    if (status === "past_due") return "Kidly+ · Payment failed";
    return "Free plan";
  })();

  const handleSwitch = async (target: PlanInterval) => {
    if (switching) return;
    setSwitching(target);
    setSwitchError(null);
    try {
      await onSwitchPlan(target);
    } catch (e) {
      setSwitchError(
        e instanceof Error ? e.message : "Couldn't switch plan.",
      );
    } finally {
      setSwitching(null);
    }
  };
  const sub = (() => {
    if (status === "trialing" && subscription?.trialEndsAt)
      return `Trial ends ${formatDate(subscription.trialEndsAt)}`;
    if (status === "active" && subscription?.renewsAt)
      return `Renews ${formatDate(subscription.renewsAt)}`;
    if (status === "canceled" && subscription?.renewsAt)
      return `Access until ${formatDate(subscription.renewsAt)}`;
    if (status === "past_due")
      return "Update your payment method to keep watching.";
    return "Subscribe to Kidly+ to unlock the full catalog.";
  })();

  return (
    <div
      className="w-respgrid"
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 320px",
        gap: 24,
        alignItems: "start",
      }}
    >
      {/* Left column — status banner, features, billing history */}
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        {/* Status banner */}
        <div
          className="w-panel"
          style={{
            background: isActive
              ? "linear-gradient(135deg, #FFE2C2 0%, #FFD86B 60%, #FFB774 100%)"
              : "var(--surface)",
            border: isActive ? 0 : "1px solid var(--line)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {isActive && (
            <div
              style={{
                position: "absolute",
                right: -20,
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
              }}
            >
              <WebMascot mood="cheer" color="#FFFFFF" size={140} />
            </div>
          )}
          <div
            style={{
              fontSize: 11,
              fontWeight: 900,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "var(--primary-ink)",
            }}
          >
            Your plan
          </div>
          <h2
            style={{
              fontFamily: "var(--display)",
              fontSize: 28,
              fontWeight: 900,
              letterSpacing: "-0.01em",
              color: "var(--ink)",
              margin: "4px 0 6px",
            }}
          >
            {headline}
          </h2>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: isActive ? "var(--primary-ink)" : "var(--ink-2)",
            }}
          >
            {sub}
          </div>
          <div
            style={{ display: "flex", gap: 8, marginTop: 18, flexWrap: "wrap" }}
          >
            {isActive ? (
              <>
                {/* Plan switcher — only show the other direction. Most
                    parents start on monthly and upgrade to annual; the
                    reverse path is rarer but supported. */}
                {plan === "monthly" && (
                  <button
                    className="btn"
                    style={{ padding: "10px 16px", fontSize: 13 }}
                    onClick={() => handleSwitch("annual")}
                    disabled={switching !== null}
                  >
                    {switching === "annual"
                      ? "Switching…"
                      : "Switch to annual · save 20%"}
                  </button>
                )}
                {plan === "annual" && (
                  <button
                    className="btn btn-ghost"
                    style={{
                      background: "rgba(255,255,255,0.6)",
                      padding: "10px 16px",
                      fontSize: 13,
                    }}
                    onClick={() => handleSwitch("monthly")}
                    disabled={switching !== null}
                  >
                    {switching === "monthly"
                      ? "Switching…"
                      : "Switch to monthly"}
                  </button>
                )}
                <button
                  className="btn btn-ghost"
                  style={{
                    background: "rgba(255,255,255,0.6)",
                    padding: "10px 16px",
                    fontSize: 13,
                  }}
                  onClick={onManageSubscription}
                  disabled={switching !== null}
                >
                  Manage subscription
                </button>
              </>
            ) : (
              <>
                {/* When eligible, the no-card free trial is the primary CTA
                    — zero friction, no Stripe redirect. The paid plans drop
                    to secondary. Otherwise (trial used) show the paid CTAs. */}
                {trialEligible && (
                  <button
                    className="btn"
                    style={{ padding: "10px 16px", fontSize: 13 }}
                    onClick={handleStartTrial}
                    disabled={startingTrial}
                  >
                    {startingTrial
                      ? "Starting…"
                      : "Start 30-day free trial · no card"}
                  </button>
                )}
                <button
                  className={trialEligible ? "btn btn-ghost" : "btn"}
                  style={{
                    background: trialEligible
                      ? "rgba(255,255,255,0.6)"
                      : undefined,
                    padding: "10px 16px",
                    fontSize: 13,
                  }}
                  onClick={() => onSubscribe("annual")}
                >
                  {trialEligible
                    ? "Subscribe · annual (save 20%)"
                    : "Try Kidly+ · annual (save 20%)"}
                </button>
                <button
                  className="btn btn-ghost"
                  style={{
                    background: "rgba(255,255,255,0.6)",
                    padding: "10px 16px",
                    fontSize: 13,
                  }}
                  onClick={() => onSubscribe("monthly")}
                >
                  Or monthly · $0.10/mo
                </button>
              </>
            )}
          </div>
          {switchError && (
            <div
              style={{
                marginTop: 12,
                fontSize: 12.5,
                fontWeight: 700,
                color: "#C0392B",
              }}
            >
              {switchError}
            </div>
          )}
        </div>

        <div className="w-panel">
          <h3>What&apos;s included</h3>
          <div
            className="w-respgrid2"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
            }}
          >
            {features.map((f) => (
              <div
                key={f}
                style={{ display: "flex", alignItems: "center", gap: 10 }}
              >
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 6,
                    background: "var(--bg-warm)",
                    color: "var(--primary-ink)",
                    display: "grid",
                    placeItems: "center",
                    flex: "0 0 auto",
                  }}
                >
                  <Icon name="check" size={14} />
                </div>
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "var(--ink)",
                  }}
                >
                  {f}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Billing history — empty until we wire a Stripe invoices fetch.
            The panel still renders so the layout matches the design and so
            the surface feels intentional (not "missing"). */}
        <div className="w-panel">
          <h3>Billing history</h3>
          {isActive ? (
            <div
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: "var(--ink-2)",
                lineHeight: 1.55,
              }}
            >
              Invoices appear here after each renewal. Open{" "}
              <button
                onClick={onManageSubscription}
                style={{
                  background: "transparent",
                  border: 0,
                  padding: 0,
                  color: "var(--primary-ink)",
                  fontWeight: 800,
                  textDecoration: "underline",
                  cursor: "pointer",
                  font: "inherit",
                }}
              >
                Manage subscription
              </button>{" "}
              to download PDF receipts from Stripe.
            </div>
          ) : (
            <div
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: "var(--ink-2)",
                lineHeight: 1.55,
              }}
            >
              You haven&apos;t been billed yet. Subscribe to Kidly+ and your
              invoices will show up here.
            </div>
          )}
        </div>
      </div>

      {/* Right column — payment method + refer-a-friend */}
      <aside style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div className="w-panel">
          <h3>Payment method</h3>
          {isActive ? (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "14px 16px",
                  background: "var(--bg-warm)",
                  borderRadius: 12,
                }}
              >
                <div
                  style={{
                    width: 46,
                    height: 32,
                    borderRadius: 6,
                    background: "linear-gradient(135deg,#1A3A8C,#3856A8)",
                    color: "#fff",
                    fontSize: 11,
                    fontWeight: 900,
                    letterSpacing: "0.06em",
                    display: "grid",
                    placeItems: "center",
                    flex: "0 0 auto",
                  }}
                >
                  CARD
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 800,
                      fontSize: 14,
                      color: "var(--ink)",
                    }}
                  >
                    Managed by Stripe
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "var(--ink-3)",
                    }}
                  >
                    Open the portal to view or change
                  </div>
                </div>
              </div>
              <button
                className="btn btn-ghost"
                style={{
                  background: "var(--surface)",
                  marginTop: 12,
                  width: "100%",
                  fontSize: 13,
                }}
                onClick={onManageSubscription}
              >
                Update card
              </button>
            </>
          ) : (
            <div
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: "var(--ink-2)",
                lineHeight: 1.5,
              }}
            >
              Add a card when you subscribe to Kidly+ — we never store card
              details on our servers.
            </div>
          )}
        </div>

      </aside>
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Kids tab — grid of the parent's children with quick stats and an
// "Add a child" placeholder card up to the 4-kid plan limit.
// ─────────────────────────────────────────────────────────────────────────
function KidsTab({
  kids,
  onAddChild,
  onSwitchChild,
  onKidsChanged,
}: {
  kids: ChildWithProgress[];
  onAddChild: () => void;
  onSwitchChild?: (childId: string) => void;
  onKidsChanged?: () => void;
}) {
  const maxKids = 4;
  const { accessToken } = useAuthStore();
  // Per-kid editing. This used to be a dead end on web ("Per-kid edit screen
  // lives on mobile today"), and then a name/age pair — neither of which let a
  // parent change the birthday, mascot colour or interests they picked when the
  // profile was created. The form now matches Add a child field for field.
  const [editing, setEditing] = useState<ChildWithProgress | null>(null);
  const [editName, setEditName] = useState("");
  const [birthMonth, setBirthMonth] = useState<number | null>(null);
  const [birthYear, setBirthYear] = useState<number | null>(null);
  const [color, setColor] = useState<string>(mascotColors[0]);
  const [picked, setPicked] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [loadingInterests, setLoadingInterests] = useState(false);

  const birthday =
    birthMonth !== null && birthYear !== null
      ? new Date(birthYear, birthMonth, 1)
      : null;
  const previewAge = birthday ? ageFromBirthday(birthday) : null;
  const years = birthYearOptions(birthYear);

  const openEdit = (kid: ChildWithProgress) => {
    setEditing(kid);
    setEditName(kid.name ?? "");
    setColor(kid.color || mascotColors[0]);
    if (kid.birthday) {
      const d = new Date(kid.birthday);
      if (!Number.isNaN(d.getTime())) {
        setBirthMonth(d.getMonth());
        setBirthYear(d.getFullYear());
      }
    } else {
      // Older profiles predate the birthday field. Derive a birth year from the
      // stored age so the Year select isn't blank; leave the month unset so it's
      // clear the parent should confirm it.
      setBirthMonth(null);
      setBirthYear(kid.age != null ? new Date().getFullYear() - kid.age : null);
    }
    // Interests live on the kid's parental Controls, not the Child row.
    setPicked([]);
    if (accessToken) {
      setLoadingInterests(true);
      childrenApi
        .getControls(kid.id, accessToken)
        .then((c) => setPicked(c?.categories ?? []))
        .catch(() => {
          /* no controls row yet — start with nothing selected */
        })
        .finally(() => setLoadingInterests(false));
    }
  };

  const toggleInterest = (i: string) =>
    setPicked((cur) =>
      cur.includes(i) ? cur.filter((x) => x !== i) : [...cur, i],
    );

  const saveEdit = async () => {
    if (!editing || !accessToken || saving) return;
    const trimmed = editName.trim();
    if (!trimmed) {
      webAlert("A name is required.");
      return;
    }
    if (!birthday) {
      webAlert("Please pick a birthday (month and year).");
      return;
    }
    setSaving(true);
    try {
      // Always send the birthday, never a bare age — the backend derives age
      // from birthday, so writing age alone leaves the two disagreeing.
      await childrenApi.update(
        editing.id,
        {
          name: trimmed,
          age: ageFromBirthday(birthday),
          birthday: birthday.toISOString(),
          color,
        },
        accessToken,
      );
      try {
        await childrenApi.updateControls(
          editing.id,
          { categories: picked },
          accessToken,
        );
      } catch {
        /* non-fatal — the profile itself saved */
      }
      setEditing(null);
      onKidsChanged?.();
    } catch (e) {
      webAlert(e instanceof Error ? e.message : "Couldn't save the profile.");
    } finally {
      setSaving(false);
    }
  };

  const deleteKid = async () => {
    if (!editing || !accessToken) return;
    const ok = await webConfirm(
      `Delete ${editing.name}'s profile? This removes their watch history and stickers. This can't be undone.`,
      { confirmLabel: "Delete profile", destructive: true },
    );
    if (!ok) return;
    setSaving(true);
    try {
      await childrenApi.remove(editing.id, accessToken);
      setEditing(null);
      onKidsChanged?.();
    } catch (e) {
      webAlert(e instanceof Error ? e.message : "Couldn't delete the profile.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
        }}
      >
        <div>
          <div
            style={{
              fontFamily: "var(--display)",
              fontWeight: 900,
              fontSize: 22,
              color: "var(--ink)",
            }}
          >
            Kid profiles ({kids.length} of {maxKids})
          </div>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: "var(--ink-3)",
              marginTop: 4,
            }}
          >
            Up to {maxKids} children on a Kidly+ Family plan.
          </div>
        </div>
        <button
          className="btn"
          style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
          onClick={onAddChild}
          disabled={kids.length >= maxKids}
        >
          <Icon name="plus" size={14} /> Add a child
        </button>
      </div>

      <div
        className="w-respgrid2"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 16,
        }}
      >
        {kids.map((k) => {
          const stars = k.progress?.correctAnswers ?? 0;
          const todayMin = Math.round(
            (k.progress?.todayWatchedSeconds ?? 0) / 60,
          );
          const dailyLimit = k.controls?.dailyMinutes ?? 60;
          return (
            <div
              key={k.id}
              className="w-panel"
              style={{
                position: "relative",
                border: "1px solid var(--line)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  marginBottom: 14,
                }}
              >
                <div
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: "50%",
                    background: k.color ?? "var(--bg-warm)",
                    overflow: "hidden",
                    display: "grid",
                    placeItems: "center",
                    flex: "0 0 auto",
                  }}
                >
                  <WebMascot mood="happy" color="#fff" size={42} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontFamily: "var(--display)",
                      fontSize: 22,
                      fontWeight: 900,
                      color: "var(--ink)",
                      letterSpacing: "-0.01em",
                      lineHeight: 1.0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {k.name}
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "var(--ink-3)",
                      marginTop: 4,
                    }}
                  >
                    Age {k.age}
                  </div>
                </div>
              </div>

              <div
                className="w-respgrid2"
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 8,
                  marginBottom: 14,
                }}
              >
                <Stat label="Stars" v={String(stars)} />
                <Stat
                  label="Today"
                  v={`${todayMin}m / ${dailyLimit}m`}
                />
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                {onSwitchChild && (
                  <button
                    className="btn btn-ghost"
                    onClick={() => onSwitchChild(k.id)}
                    style={{
                      background: "var(--surface)",
                      flex: 1,
                      fontSize: 13,
                    }}
                  >
                    Switch
                  </button>
                )}
                <button
                  className="btn btn-ghost"
                  style={{
                    background: "var(--surface)",
                    flex: 1,
                    fontSize: 13,
                  }}
                  onClick={() => openEdit(k)}
                >
                  Edit
                </button>
              </div>
            </div>
          );
        })}

        {kids.length < maxKids && (
          <button
            onClick={onAddChild}
            style={{
              border: "2px dashed var(--line-strong)",
              background: "transparent",
              borderRadius: 18,
              padding: 22,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              cursor: "pointer",
              color: "var(--ink-3)",
              font: "inherit",
              minHeight: 200,
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                background: "var(--bg-warm)",
                color: "var(--primary-ink)",
                display: "grid",
                placeItems: "center",
              }}
            >
              <Icon name="plus" size={26} />
            </div>
            <div
              style={{
                fontFamily: "var(--display)",
                fontWeight: 900,
                fontSize: 16,
                color: "var(--ink)",
              }}
            >
              Add a child
            </div>
            <div style={{ fontSize: 12, fontWeight: 600 }}>
              {maxKids - kids.length} slots left
            </div>
          </button>
        )}
      </div>

      {/* Per-kid edit modal */}
      {editing && (
        <div
          onClick={() => !saving && setEditing(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 200,
            background: "rgba(20,12,8,0.62)",
            backdropFilter: "blur(3px)",
            display: "grid",
            placeItems: "center",
            padding: 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 420,
              background: "var(--surface)",
              borderRadius: 24,
              padding: 26,
              boxShadow: "0 24px 60px rgba(20,12,8,0.35)",
              // The form grew past a laptop viewport once birthday, colour and
              // interests joined it — scroll inside the sheet, not the page.
              maxHeight: "calc(100vh - 48px)",
              overflowY: "auto",
            }}
          >
            <h2
              style={{
                fontFamily: "var(--display)",
                fontSize: 22,
                fontWeight: 900,
                color: "var(--ink)",
                margin: "0 0 4px",
              }}
            >
              Edit {editing.name}
            </h2>
            <p
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: "var(--ink-3)",
                margin: "0 0 18px",
              }}
            >
              Update their details, mascot colour and what they like to watch.
            </p>

            <label className="w-field-label" htmlFor="kid-name">
              Name
            </label>
            <input
              id="kid-name"
              className="w-input"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Child's name"
              style={{ marginBottom: 14 }}
            />

            <label className="w-field-label">Birthday</label>
            <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
              <select
                className="w-input"
                aria-label="Birth month"
                value={birthMonth ?? ""}
                onChange={(e) =>
                  setBirthMonth(e.target.value === "" ? null : Number(e.target.value))
                }
                style={{ flex: 1 }}
              >
                <option value="">Month</option>
                {MONTH_LABELS.map((m, i) => (
                  <option key={m} value={i}>
                    {m}
                  </option>
                ))}
              </select>
              <select
                className="w-input"
                aria-label="Birth year"
                value={birthYear ?? ""}
                onChange={(e) =>
                  setBirthYear(e.target.value === "" ? null : Number(e.target.value))
                }
                style={{ flex: 1 }}
              >
                <option value="">Year</option>
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "var(--ink-3)",
                marginTop: -8,
                marginBottom: 14,
              }}
            >
              {previewAge !== null ? `${previewAge} years old` : "Age not set"}
            </div>

            <label className="w-field-label">Mascot colour</label>
            <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
              {mascotColors.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label={`Mascot colour ${c}`}
                  aria-pressed={c === color}
                  onClick={() => setColor(c)}
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 17,
                    background: c,
                    border:
                      c === color
                        ? "3px solid var(--ink)"
                        : "3px solid transparent",
                    cursor: "pointer",
                    padding: 0,
                  }}
                />
              ))}
            </div>

            <label className="w-field-label">
              What do they love?
              {loadingInterests && (
                <span style={{ fontWeight: 600, color: "var(--ink-3)" }}>
                  {" "}
                  loading…
                </span>
              )}
            </label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {CONTENT_CATEGORIES.map((i) => {
                const on = picked.includes(i);
                return (
                  <button
                    key={i}
                    type="button"
                    aria-pressed={on}
                    onClick={() => toggleInterest(i)}
                    style={{
                      font: "inherit",
                      fontSize: 13,
                      fontWeight: 800,
                      padding: "7px 13px",
                      borderRadius: 999,
                      cursor: "pointer",
                      color: on ? "#fff" : "var(--ink-2)",
                      background: on ? "var(--ink)" : "transparent",
                      border: `1.5px solid ${on ? "var(--ink)" : "var(--line-strong)"}`,
                    }}
                  >
                    {i}
                  </button>
                );
              })}
            </div>

            <div
              style={{
                display: "flex",
                gap: 10,
                marginTop: 22,
                alignItems: "center",
              }}
            >
              <button
                className="btn"
                onClick={saveEdit}
                disabled={saving}
                style={{ flex: 1 }}
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
              <button
                className="btn btn-ghost"
                onClick={() => setEditing(null)}
                disabled={saving}
                style={{ background: "var(--surface)" }}
              >
                Cancel
              </button>
            </div>

            <button
              onClick={deleteKid}
              disabled={saving}
              style={{
                display: "block",
                margin: "16px auto 0",
                border: 0,
                background: "transparent",
                font: "inherit",
                fontWeight: 800,
                fontSize: 14,
                color: "#C0453F",
                cursor: saving ? "default" : "pointer",
              }}
            >
              Delete profile
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, v }: { label: string; v: string }) {
  return (
    <div
      style={{
        background: "var(--bg-warm)",
        padding: "10px 12px",
        borderRadius: 10,
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "var(--ink-3)",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: "var(--display)",
          fontWeight: 900,
          fontSize: 18,
          color: "var(--ink)",
          letterSpacing: "-0.01em",
        }}
      >
        {v}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Devices tab — until the backend tracks paired devices, the design's list
// would be entirely fabricated. We render an empty state with the same
// "Pair a new device" CTA so the surface feels alive but doesn't lie.
// ─────────────────────────────────────────────────────────────────────────
function DevicesTab({ onPairTV }: { onPairTV: () => void }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
        }}
      >
        <div>
          <div
            style={{
              fontFamily: "var(--display)",
              fontWeight: 900,
              fontSize: 22,
              color: "var(--ink)",
            }}
          >
            Paired devices
          </div>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: "var(--ink-3)",
              marginTop: 4,
            }}
          >
            Where Kidly is signed in right now.
          </div>
        </div>
        <button
          className="btn"
          style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
          onClick={onPairTV}
        >
          <Icon name="plus" size={14} /> Pair a new device
        </button>
      </div>

      <div
        className="w-panel"
        style={{
          textAlign: "center",
          padding: "40px 24px",
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 16,
            background: "var(--bg-warm)",
            color: "var(--primary-ink)",
            display: "grid",
            placeItems: "center",
            margin: "0 auto 14px",
          }}
        >
          <Icon name="cast" size={26} />
        </div>
        <div
          style={{
            fontFamily: "var(--display)",
            fontWeight: 900,
            fontSize: 18,
            color: "var(--ink)",
            marginBottom: 4,
          }}
        >
          No paired devices yet
        </div>
        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: "var(--ink-2)",
            maxWidth: 360,
            margin: "0 auto 16px",
            lineHeight: 1.5,
          }}
        >
          Pair the Kidly TV app to send shows to the living room. We&apos;ll
          remember each TV so kids can launch them on their own.
        </div>
        <button className="btn" onClick={onPairTV}>
          Pair a TV
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Notifications tab — toggles live in local state until we wire a real
// notifications-preferences endpoint. The state intentionally does NOT
// persist; the design just needs the visual surface.
// ─────────────────────────────────────────────────────────────────────────
// All six rows are live, and all six are delivered by email. There is no
// APNs/FCM pipeline, so rows the design described as "push" arrive by mail — the
// descriptions say so rather than promising a push that can't happen.
//
// "Watch requests" originally read "when a child asks to watch a video that
// needs review", but no kid-initiated request feature exists anywhere in the
// product. The review flow that does exist is a parent submitting their own
// generated video for admin approval, so the row was relabelled to match what
// the toggle actually controls.
const NOTIFICATION_ROWS: {
  key: keyof NotificationPrefs;
  name: string;
  desc: string;
}[] = [
  {
    key: "weeklyRecap",
    name: "Weekly recap email",
    desc: "Sunday morning, with your kids' highlights.",
  },
  {
    key: "stickerUnlocked",
    name: "New sticker unlocked",
    desc: "Email when a child earns a new sticker.",
  },
  {
    key: "watchRequests",
    name: "Review decisions",
    desc: "When a video you submitted is approved or rejected.",
  },
  {
    key: "bedtimeReminders",
    name: "Bedtime reminders",
    desc: "Heads-up when a kid has about 5 minutes of screen time left.",
  },
  {
    key: "billing",
    name: "Billing & receipts",
    desc: "Payment receipts and failed-payment alerts.",
  },
  {
    key: "productNews",
    name: "Product news",
    desc: "Occasional updates about Kidly features.",
  },
];

function NotifyTab() {
  const { accessToken } = useAuthStore();
  const [prefs, setPrefs] = useState<NotificationPrefs | null>(null);
  const [failed, setFailed] = useState(false);
  // Browser push is a delivery channel, not a seventh notification: the six
  // toggles decide *what* is sent, this decides whether this browser also gets
  // it as a desktop notification on top of the email.
  const [push, setPush] = useState<PushState>("unsupported");
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    if (!pushSupported()) return;
    currentPushState().then(setPush).catch(() => setPush("unsupported"));
  }, []);

  const togglePush = async () => {
    if (!accessToken || pushBusy) return;
    setPushBusy(true);
    try {
      if (push === "on") {
        await disablePush(accessToken);
        setPush("off");
      } else {
        await enablePush(accessToken);
        setPush("on");
      }
    } catch (e) {
      webAlert(e instanceof Error ? e.message : "Couldn't change notifications.");
      setPush(await currentPushState());
    } finally {
      setPushBusy(false);
    }
  };

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    notificationsApi
      .get(accessToken)
      .then((p) => {
        if (!cancelled) setPrefs(p);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  const toggle = async (k: keyof NotificationPrefs) => {
    if (!prefs || !accessToken) return;
    const next = !prefs[k];
    // Optimistic — the switch should feel instant; roll back if the write loses.
    setPrefs({ ...prefs, [k]: next });
    try {
      const saved = await notificationsApi.update({ [k]: next }, accessToken);
      setPrefs(saved);
    } catch (e) {
      setPrefs((cur) => (cur ? { ...cur, [k]: !next } : cur));
      webAlert(
        e instanceof Error ? e.message : "Couldn't save that setting.",
      );
    }
  };

  return (
    <div style={{ maxWidth: 720 }}>
      {push !== "unsupported" && (
        <div className="w-panel" style={{ marginBottom: 16 }}>
          <h3>This browser</h3>
          <div className="w-row">
            <div>
              <div className="name">Desktop notifications</div>
              <div className="desc">
                {push === "denied"
                  ? "Blocked in your browser settings — allow notifications for this site to turn them on."
                  : "Also show the notifications below as desktop alerts, even when Kidly isn't open."}
              </div>
            </div>
            <button
              className={"w-switch " + (push === "on" ? "on" : "")}
              onClick={togglePush}
              disabled={pushBusy || push === "denied"}
              aria-label="Toggle desktop notifications"
              aria-pressed={push === "on"}
              style={
                pushBusy || push === "denied"
                  ? { opacity: 0.5, cursor: "default" }
                  : undefined
              }
            />
          </div>
        </div>
      )}

      <div className="w-panel">
        <h3>Email notifications</h3>
        {failed && (
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: "#C0453F",
              padding: "6px 0 10px",
            }}
          >
            Couldn't load your notification settings.
          </div>
        )}
        {NOTIFICATION_ROWS.map(({ key, name, desc }) => (
          <div key={key} className="w-row">
            <div>
              <div className="name">{name}</div>
              <div className="desc">{desc}</div>
            </div>
            <button
              className={"w-switch " + (prefs?.[key] ? "on" : "")}
              onClick={() => toggle(key)}
              disabled={!prefs}
              aria-label={`Toggle ${name}`}
              aria-pressed={!!prefs?.[key]}
              style={!prefs ? { opacity: 0.5, cursor: "default" } : undefined}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Privacy tab — copy-heavy. The danger zone's "Delete account" really deletes
// (DELETE /v1/auth/me) after a confirm, then signs the parent out.
// ─────────────────────────────────────────────────────────────────────────
function PrivacyTab({
  onSignOut,
  onDeleteAccount,
  onGoAdminMetrics,
}: {
  onSignOut: () => void;
  onDeleteAccount: () => Promise<void> | void;
  onGoAdminMetrics?: () => void;
}) {
  const [deleting, setDeleting] = useState(false);
  const onDelete = async () => {
    const ok = await webConfirm(
      "Permanently delete your Kidly account and all kid data? This cannot be undone.",
      { confirmLabel: "Delete account", destructive: true },
    );
    if (!ok) return;
    setDeleting(true);
    try {
      await onDeleteAccount();
    } catch (e) {
      setDeleting(false);
      webAlert(
        e instanceof Error ? e.message : "Couldn't delete your account.",
      );
    }
  };

  return (
    <div style={{ maxWidth: 720, display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="w-panel">
        <h3>Children&apos;s data</h3>
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: "var(--ink-2)",
            lineHeight: 1.55,
            marginBottom: 14,
          }}
        >
          We store only what we need to personalize Kidly: watch history, quiz
          answers, and stars. Kidly never shows ads to children, never sells
          data, and is COPPA-compliant.
        </div>
        <a
          className="btn btn-ghost"
          href="https://kidly.family/privacy"
          target="_blank"
          rel="noreferrer noopener"
          style={{
            background: "var(--surface)",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            textDecoration: "none",
          }}
        >
          <Icon name="sparkle" size={14} /> Read the full privacy policy
        </a>
      </div>

      <div className="w-panel">
        <h3>Export your family&apos;s data</h3>
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: "var(--ink-2)",
            lineHeight: 1.55,
            marginBottom: 14,
          }}
        >
          Download everything we have on file — watch history, profiles,
          stars, and questions. Delivered as a JSON file you can keep.
        </div>
        <button
          className="btn"
          onClick={() =>
            webAlert(
              "Data export is coming soon — email support@kidly.family and we'll send it within 72 hours.",
            )
          }
        >
          Request data export
        </button>
      </div>

      <div className="w-panel">
        <h3>Sign out</h3>
        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: "var(--ink-2)",
            lineHeight: 1.5,
            marginBottom: 14,
          }}
        >
          Sign out of this browser. Your TV and other devices stay signed in.
        </div>
        <button
          className="btn btn-ghost"
          onClick={onSignOut}
          style={{
            background: "var(--surface)",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <Icon name="logout" size={14} /> Sign out
        </button>
      </div>

      <div
        className="w-panel"
        style={{ border: "1px solid #F5C7C7", background: "#FFF7F7" }}
      >
        <h3 style={{ color: "#7A2E2E" }}>Danger zone</h3>
        <div className="w-row">
          <div>
            <div className="name" style={{ color: "#7A2E2E" }}>
              Delete account
            </div>
            <div className="desc">
              Permanently erase your household and all kid data.
            </div>
          </div>
          <button
            className="btn"
            onClick={onDelete}
            disabled={deleting}
            style={{
              background: "#C44",
              boxShadow:
                "inset 0 -3px 0 rgba(0,0,0,0.18), 0 8px 18px rgba(196,68,68,0.30)",
              opacity: deleting ? 0.7 : 1,
            }}
          >
            {deleting ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>

      {/* Discreet link to the internal admin dashboard. Always rendered
          when the host wired the callback — the backend AdminGuard is the
          real gate. Non-admins who click see an error inline, no data. */}
      {onGoAdminMetrics && (
        <div
          style={{
            paddingTop: 8,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <button
            type="button"
            onClick={onGoAdminMetrics}
            style={{
              background: "transparent",
              border: 0,
              padding: 0,
              color: "var(--ink-3)",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            Operational metrics (internal)
          </button>
        </div>
      )}
    </div>
  );
}

// Best-effort JWT-payload decode that pulls the email out without an extra
// network call. Returns null on any parse failure so the consumer falls
// back to "—". Browser-only: `atob` isn't on RN without a polyfill, but
// this file is web-only.
export function emailFromJwt(token: string | null): string | null {
  if (!token) return null;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const padded =
      parts[1] + "=".repeat((4 - (parts[1].length % 4)) % 4);
    const payload = JSON.parse(
      atob(padded.replace(/-/g, "+").replace(/_/g, "/")),
    );
    return typeof payload?.email === "string" ? payload.email : null;
  } catch {
    return null;
  }
}

// Re-export the hook so WebShell can read the access token without
// importing it separately. Convenience only.
export { useAuthStore };

export default WebFamilyAccount;
